import React, { useState, useEffect } from "react";
import {
    Database,
    Server,
    HardDrive,
    Shield,
    AlertTriangle,
    XCircle,
    RefreshCw,
    Cpu,
    Activity,
    Disc,
    FolderGit2,
    Clock,
    ChevronDown,
    ChevronUp,
} from "lucide-react";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";

interface RaidProps {
    agent_id: string;
    organization_id: string;
    asset_id: string;
    client_id: string;
}

const healthColor = (health?: string) => {
    const h = health?.toUpperCase() || "";
    if (h === "OK" || h === "HEALTHY" || h === "ONLINE") return "text-emerald-600 dark:text-emerald-400";
    if (h === "WARNING" || h === "DEGRADED") return "text-amber-600 dark:text-amber-400";
    if (h === "FAILED" || h === "OFFLINE" || h === "ERROR") return "text-rose-600 dark:text-rose-400";
    return "text-slate-500 dark:text-gray-400";
};

const healthBgBorder = (health?: string) => {
    const h = health?.toUpperCase() || "";
    if (h === "OK" || h === "HEALTHY" || h === "ONLINE") return "bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/20 text-emerald-700 dark:text-emerald-400";
    if (h === "WARNING" || h === "DEGRADED") return "bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/20 text-amber-700 dark:text-amber-400";
    if (h === "FAILED" || h === "OFFLINE" || h === "ERROR") return "bg-rose-50 dark:bg-rose-500/10 border-rose-200 dark:border-rose-500/20 text-rose-700 dark:text-rose-400";
    return "bg-slate-100 dark:bg-gray-500/10 border-slate-200 dark:border-gray-500/20 text-slate-600 dark:text-gray-400";
};

const LogicalDriveDetailsSection: React.FC<{ mountPoints: string[]; nestedPhysical: any[] }> = ({ mountPoints, nestedPhysical }) => {
    const [expanded, setExpanded] = useState(false);

    const hasMountPoints = mountPoints && mountPoints.length > 0;
    const hasNestedPhysical = nestedPhysical && nestedPhysical.length > 0;

    if (!hasMountPoints && !hasNestedPhysical) return null;

    return (
        <div className="mt-2.5 border-t border-purple-200 dark:border-purple-500/15 pt-2">
            <button
                type="button"
                onClick={() => setExpanded((prev) => !prev)}
                className="flex items-center justify-between w-full text-left group cursor-pointer focus:outline-none select-none py-1 px-1 rounded hover:bg-purple-100/50 dark:hover:bg-purple-500/10 transition-colors"
            >
                <div className="flex items-center gap-2 text-[9px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-wider group-hover:text-purple-700 dark:group-hover:text-purple-300 transition-colors flex-wrap">
                    {hasMountPoints && <span>Partitions & Mount Points ({mountPoints.length})</span>}
                    {hasMountPoints && hasNestedPhysical && <span className="text-slate-400 dark:text-gray-600 font-normal">•</span>}
                    {hasNestedPhysical && <span>Member Physical Drives ({nestedPhysical.length})</span>}
                </div>
                <div className="p-0.5 rounded text-slate-400 dark:text-gray-400 group-hover:text-purple-700 dark:group-hover:text-purple-300 transition-all shrink-0">
                    {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                </div>
            </button>

            {expanded && (
                <div className="space-y-2.5 mt-2 transition-all">
                    {/* Partitions & Mount Points */}
                    {hasMountPoints && (
                        <div>
                            <p className="text-[9px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                                Partitions & Mount Points ({mountPoints.length})
                            </p>
                            <div className="flex flex-wrap gap-1">
                                {mountPoints.map((mp, mpidx) => (
                                    <span
                                        key={mpidx}
                                        className="px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-200 border border-blue-200 dark:border-blue-800/40 text-[9px] font-mono"
                                    >
                                        {mp}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Member Physical Drives */}
                    {hasNestedPhysical && (
                        <div className={hasMountPoints ? "pt-2 border-t border-purple-200 dark:border-purple-500/10" : ""}>
                            <p className="text-[9px] font-bold text-cyan-600 dark:text-cyan-400 uppercase tracking-wider mb-1">
                                Member Physical Drives ({nestedPhysical.length})
                            </p>
                            <div className="space-y-1">
                                {nestedPhysical.map((pd: any, pdidx: number) => (
                                    <div key={pdidx} className="bg-white dark:bg-gray-900/50 p-1.5 rounded flex items-center justify-between text-[10px] border border-slate-200 dark:border-gray-800 shadow-sm dark:shadow-none">
                                        <span className="text-slate-700 dark:text-gray-300 font-medium truncate max-w-[180px]">{pd.model || `Bay ${pd.location}`}</span>
                                        <span className="text-slate-500 dark:text-gray-400 font-mono">{pd.size}</span>
                                        <span className={`font-bold ${healthColor(pd.status || pd.state)}`}>{pd.status || pd.state || "OK"}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const Raid: React.FC<RaidProps> = ({
    agent_id,
    organization_id,
    asset_id,
    client_id,
}) => {
    const [raid, setRaid] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    async function fetchRaid() {
        setLoading(true);
        try {
            const params = {
                query: JSON.stringify({
                    client_id,
                    organization_id,
                    agent_id,
                    asset_id,
                }),
            };
            const response = await baseapi.get("/asset_raid", { params });
            const dataItem = Array.isArray(response.data?.data)
                ? response.data?.data?.[0]
                : response.data?.data;
            const rawResults = dataItem?.results ?? response.data?.results ?? dataItem;

            let parsedResults = null;
            if (typeof rawResults === "string") {
                try {
                    parsedResults = JSON.parse(rawResults);
                } catch {
                    parsedResults = null;
                }
            } else {
                parsedResults = rawResults ?? null;
            }

            setRaid(parsedResults);
        } catch (e: any) {
            showError(e.message || "Failed to load RAID info");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (asset_id || agent_id) {
            fetchRaid();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agent_id, asset_id, organization_id, client_id]);

    if (loading) {
        return (
            <div className="flex items-center justify-center p-8 space-x-3 text-slate-500 dark:text-gray-400 text-xs">
                <div className="w-5 h-5 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
                <span>Loading RAID & Storage Information...</span>
            </div>
        );
    }

    // Safely extract controllers list whether rawControllers is an array or object
    const rawControllers = raid?.controllers;
    const controllersList: any[] = Array.isArray(rawControllers)
        ? rawControllers
        : rawControllers
            ? [rawControllers]
            : [];

    const summary = raid?.summary || {};

    if (!raid || (controllersList.length === 0 && !raid.summary && !raid.overall_health)) {
        return (
            <div className="text-center py-8 border border-slate-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900/60 mb-4 mx-2 shadow-sm dark:shadow-none">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-3 bg-blue-50 dark:bg-blue-500/10">
                    <Database className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-sm font-semibold text-slate-800 dark:text-blue-300 mb-1">No RAID / Storage Configuration</h3>
                <p className="text-xs text-slate-500 dark:text-gray-400">No RAID controllers or storage drives detected for this asset.</p>
            </div>
        );
    }

    const overallHealth = raid?.overall_health || summary?.overall_health || "UNKNOWN";
    const totalControllers = summary?.total_controllers ?? controllersList.length;

    const totalLogicalDrives =
        summary?.total_logical_drives ??
        controllersList.reduce((acc, c) => acc + (c.logical_drives?.length || 0), 0);
    const totalPhysicalDrives =
        summary?.total_physical_drives ??
        controllersList.reduce((acc, c) => acc + (c.physical_drives?.length || 0), 0);

    return (
        <div className="p-3 text-slate-800 dark:text-gray-200">
            {/* Header */}
            <div className="flex items-center justify-between mb-4 border-b border-slate-200 dark:border-gray-700 pb-3">
                <div className="flex items-center">
                    <div className="p-1.5 bg-orange-600 rounded mr-2.5">
                        <Database className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h2 className="text-sm font-bold text-slate-900 dark:text-white leading-tight">RAID & Storage Infrastructure</h2>
                            {raid?.hostname && (
                                <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 text-[10px] text-slate-700 dark:text-gray-300 font-mono">
                                    Host: {raid.hostname}
                                </span>
                            )}
                        </div>
                        <p className="text-slate-500 dark:text-gray-400 text-[10px] uppercase font-semibold tracking-wider mt-0.5">
                            {totalControllers} controller{totalControllers === 1 ? '' : 's'} · {totalLogicalDrives} logical drive{totalLogicalDrives === 1 ? '' : 's'} · {totalPhysicalDrives} physical drive{totalPhysicalDrives === 1 ? '' : 's'}
                            {" · Health: "}
                            <span className={`font-semibold ${healthColor(overallHealth)}`}>
                                {overallHealth}
                            </span>
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {raid?.timestamp && (
                        <span className="hidden sm:flex items-center gap-1 text-[10px] text-slate-500 dark:text-gray-400 font-mono">
                            <Clock size={12} />
                            {new Date(raid.timestamp).toLocaleString()}
                        </span>
                    )}
                    <button
                        onClick={() => fetchRaid()}
                        disabled={loading}
                        className="p-2 bg-white dark:bg-gray-800 hover:bg-slate-50 dark:hover:bg-gray-700 text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:hover:text-white border border-slate-200 dark:border-gray-700 rounded-md transition-colors shadow-sm flex items-center justify-center disabled:opacity-50 h-8 cursor-pointer"
                        title="Refresh data"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-orange-500' : ''}`} />
                    </button>
                </div>
            </div>

            {/* Health Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mb-4">
                <div className="border border-slate-200 dark:border-gray-700/80 rounded-md p-2.5 flex items-center justify-between bg-white dark:bg-gray-800/30 shadow-sm dark:shadow-none">
                    <div>
                        <p className="text-slate-500 dark:text-gray-400 text-[10px] font-bold uppercase tracking-wider">Overall Health</p>
                        <p className={`text-base font-bold leading-tight mt-0.5 ${healthColor(overallHealth)}`}>
                            {overallHealth}
                        </p>
                    </div>
                    <Shield className={`w-5 h-5 ${healthColor(overallHealth)}`} />
                </div>
                <div className="border border-slate-200 dark:border-gray-700/80 rounded-md p-2.5 flex items-center justify-between bg-white dark:bg-gray-800/30 shadow-sm dark:shadow-none">
                    <div>
                        <p className="text-blue-600 dark:text-blue-400 text-[10px] font-bold uppercase tracking-wider">Controllers</p>
                        <p className="text-slate-900 dark:text-white text-base font-bold leading-tight mt-0.5">{totalControllers}</p>
                    </div>
                    <Server className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="border border-slate-200 dark:border-gray-700/80 rounded-md p-2.5 flex items-center justify-between bg-white dark:bg-gray-800/30 shadow-sm dark:shadow-none">
                    <div>
                        <p className="text-purple-600 dark:text-purple-400 text-[10px] font-bold uppercase tracking-wider">Logical Drives</p>
                        <p className="text-slate-900 dark:text-white text-base font-bold leading-tight mt-0.5">{totalLogicalDrives}</p>
                    </div>
                    <Disc className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="border border-slate-200 dark:border-gray-700/80 rounded-md p-2.5 flex items-center justify-between bg-white dark:bg-gray-800/30 shadow-sm dark:shadow-none">
                    <div>
                        <p className="text-cyan-600 dark:text-cyan-400 text-[10px] font-bold uppercase tracking-wider">Physical Drives</p>
                        <p className="text-slate-900 dark:text-white text-base font-bold leading-tight mt-0.5">
                            {totalPhysicalDrives}
                            {summary?.healthy_drives !== undefined && (
                                <span className="text-[10px] font-normal text-emerald-600 dark:text-emerald-400 ml-1.5">
                                    ({summary.healthy_drives} Healthy)
                                </span>
                            )}
                        </p>
                    </div>
                    <HardDrive className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                </div>
            </div>

            {/* Critical Issues Banner */}
            {(raid?.total_issues > 0 || (raid?.issues && raid?.issues.length > 0)) && (
                <div className="border border-rose-200 dark:border-rose-500/30 rounded-md p-3 mb-4 bg-rose-50 dark:bg-rose-500/10 shadow-sm dark:shadow-none">
                    <div className="flex items-center mb-1.5">
                        <AlertTriangle className="w-4 h-4 mr-2 text-rose-600 dark:text-rose-400" />
                        <h3 className="text-sm font-semibold text-rose-700 dark:text-rose-300">
                            Critical RAID Issues ({raid.total_issues || raid.issues?.length})
                        </h3>
                    </div>
                    <div className="space-y-1 pl-6">
                        {(raid.issues || []).map((issue: any, index: number) => (
                            <div key={index} className="flex items-start space-x-2">
                                <XCircle className="w-3.5 h-3.5 text-rose-600 dark:text-rose-400 shrink-0 mt-0.5" />
                                <span className="text-rose-800 dark:text-rose-200 text-xs leading-tight">{issue}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Controllers List */}
            <div className="space-y-4">
                <h3 className="text-[11px] font-bold text-slate-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
                    <Cpu size={14} className="text-orange-500" /> RAID Controllers ({controllersList.length})
                </h3>

                <div className="space-y-4">
                    {controllersList.map((controller: any, cidx: number) => {
                        const logicalDrives = controller.logical_drives || [];
                        const physicalDrives = controller.physical_drives || [];
                        const arraysList = controller.arrays || [];

                        return (
                            <div className="border border-slate-200 dark:border-gray-700 rounded-md overflow-hidden bg-white dark:bg-gray-900/40 shadow-sm dark:shadow-none" key={cidx}>
                                {/* Controller Header */}
                                <div className="border-b border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-800/60 px-3.5 py-2.5 flex flex-wrap items-center justify-between gap-2">
                                    <div className="flex items-center space-x-2.5 min-w-0">
                                        <div className="p-1 rounded bg-orange-50 dark:bg-blue-500/10 shrink-0">
                                            <Server className="w-4 h-4 text-orange-500" />
                                        </div>
                                        <div className="truncate">
                                            <div className="flex items-center gap-2">
                                                <h3 className="text-sm font-bold text-slate-900 dark:text-white truncate">
                                                    {controller.model || "RAID Controller"}
                                                </h3>
                                                {controller.slot && (
                                                    <span className="text-slate-600 dark:text-gray-400 text-[10px] bg-slate-100 dark:bg-gray-800 px-1.5 py-0.5 rounded border border-slate-200 dark:border-gray-700 font-mono">
                                                        Slot: {controller.slot}
                                                    </span>
                                                )}
                                            </div>
                                            {(controller.serial_number || controller.host_serial_number) && (
                                                <p className="text-[10px] text-slate-500 dark:text-gray-400 font-mono truncate">
                                                    S/N: {controller.serial_number !== "Not Available" ? controller.serial_number : controller.host_serial_number || "-"}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${healthBgBorder(controller.status)}`}>
                                            Status: {controller.status || "OK"}
                                        </span>
                                        {controller.has_raid_configured !== undefined && (
                                            <span className={`px-2 py-0.5 rounded text-[9px] font-bold border ${controller.has_raid_configured ? 'bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-500/20' : 'bg-slate-100 dark:bg-gray-700/40 text-slate-600 dark:text-gray-400 border-slate-200 dark:border-gray-600'}`}>
                                                {controller.has_raid_configured ? 'RAID Active' : 'No RAID (Passthrough)'}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="p-3.5 space-y-4">
                                    {/* Controller Hardware Specs */}
                                    <div className={`grid gap-2.5 text-xs ${arraysList.length > 0 ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" : "grid-cols-1 sm:grid-cols-2"}`}>
                                        <div className="border border-slate-200 dark:border-gray-700/60 rounded p-2.5 bg-slate-50/70 dark:bg-gray-800/30">
                                            <p className="text-[10px] font-bold text-slate-900 dark:text-white uppercase tracking-wider mb-1.5 flex items-center gap-1">
                                                <Cpu size={12} className="text-orange-500"/> Hardware & Driver
                                            </p>
                                            <div className="space-y-1 text-[11px]">
                                                <div className="flex justify-between">
                                                    <span className="text-slate-500 dark:text-gray-400">Firmware:</span>
                                                    <span className="text-slate-900 dark:text-white font-mono">{controller.firmware_version || controller.firmware_revision || "-"}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-slate-500 dark:text-gray-400">Driver Ver:</span>
                                                    <span className="text-slate-900 dark:text-white font-mono">{controller.driver_version || "-"}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-slate-500 dark:text-gray-400">HW Revision:</span>
                                                    <span className="text-slate-900 dark:text-white font-mono">{controller.hardware_revision || "-"}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="border border-slate-200 dark:border-gray-700/60 rounded p-2.5 bg-slate-50/70 dark:bg-gray-800/30">
                                            <p className="text-[10px] font-bold text-slate-900 dark:text-white uppercase tracking-wider mb-1.5 flex items-center gap-1">
                                                <Activity size={12} className="text-orange-500"/> Cache & Performance
                                            </p>
                                            <div className="space-y-1 text-[11px]">
                                                <div className="flex justify-between">
                                                    <span className="text-slate-500 dark:text-gray-400">Cache Status:</span>
                                                    <span className="text-slate-900 dark:text-white">{controller.cache_status || "-"}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-slate-500 dark:text-gray-400">Cache Size:</span>
                                                    <span className="text-slate-900 dark:text-white font-mono">{controller.cache_size || "-"}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-slate-500 dark:text-gray-400">Battery Status:</span>
                                                    <span className="text-slate-900 dark:text-white">{controller.battery_status || "-"}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {arraysList.length > 0 && (
                                            <div className="border border-slate-200 dark:border-gray-700/60 rounded p-2.5 bg-slate-50/70 dark:bg-gray-800/30">
                                                <p className="text-[10px] font-bold text-slate-900 dark:text-white uppercase tracking-wider mb-1.5 flex items-center gap-1">
                                                    <FolderGit2 size={12} className="text-orange-500"/> Arrays ({arraysList.length})
                                                </p>
                                                <div className="space-y-1 text-[11px]">
                                                    {arraysList.map((arr: any, arrIdx: number) => (
                                                        <div key={arrIdx} className="flex justify-between items-center">
                                                            <span className="text-slate-700 dark:text-gray-300 font-bold">Array {arr.id}:</span>
                                                            <span className="text-slate-900 dark:text-white font-mono">{arr.interface_type || "NVMe"} ({arr.status})</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Side-by-Side Drives Grid: Left = Logical Drives, Right = Physical Drives */}
                                    {(logicalDrives.length > 0 || physicalDrives.length > 0) && (
                                        <div className={`grid gap-4 ${logicalDrives.length > 0 && physicalDrives.length > 0 ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1"}`}>
                                            {/* Left Column: Logical Drives */}
                                            {logicalDrives.length > 0 && (
                                                <div className="space-y-3">
                                                    <h4 className="text-xs font-bold text-slate-900 dark:text-white flex items-center uppercase tracking-wide gap-1.5 border-b border-purple-200 dark:border-purple-500/20 pb-1.5">
                                                        <Database className="w-3.5 h-3.5 text-orange-500" /> Logical Drives ({logicalDrives.length})
                                                    </h4>
                                                    <div className="space-y-3">
                                                        {logicalDrives.map((ld: any, ldx: number) => {
                                                            const mountPoints: string[] = ld.mount_points || [];
                                                            const nestedPhysical: any[] = ld.physical_drives || [];

                                                            return (
                                                                <div key={ldx} className="border border-purple-200 dark:border-purple-500/20 rounded-md p-3 bg-purple-50/40 dark:bg-purple-950/10 flex flex-col justify-between space-y-2.5 shadow-sm dark:shadow-none">
                                                                    <div>
                                                                        <div className="flex items-center justify-between gap-2 mb-1.5">
                                                                            <div className="flex items-center gap-2 min-w-0">
                                                                                <div className="w-2 h-2 bg-purple-500 dark:bg-purple-400 rounded-full shrink-0"></div>
                                                                                <h5 className="text-xs font-bold text-slate-900 dark:text-white truncate">
                                                                                    LD {ld.id !== undefined ? ld.id : ldx + 1} - {ld.logical_drive_label || ld.device_name || `Logical Drive ${ld.id}`}
                                                                                </h5>
                                                                            </div>
                                                                            <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase border shrink-0 ${healthBgBorder(ld.status || ld.state)}`}>
                                                                                {ld.status || ld.state || "Online"}
                                                                            </span>
                                                                        </div>

                                                                        {/* Spec Tags */}
                                                                        <div className="flex flex-wrap gap-1.5 my-2">
                                                                            <span className="px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-500/10 text-purple-800 dark:text-purple-300 border border-purple-200 dark:border-purple-500/20 text-[10px] font-semibold">
                                                                                Size: {ld.size || "-"}
                                                                            </span>
                                                                            <span className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-gray-800 text-slate-700 dark:text-gray-300 border border-slate-200 dark:border-gray-700 text-[10px]">
                                                                                RAID: {ld.raid_level || "None"}
                                                                            </span>
                                                                            {ld.bus_protocol && (
                                                                                <span className="px-1.5 py-0.5 rounded bg-cyan-100 dark:bg-cyan-500/10 text-cyan-800 dark:text-cyan-300 border border-cyan-200 dark:border-cyan-500/20 text-[10px]">
                                                                                Bus: {ld.bus_protocol}
                                                                            </span>
                                                                            )}
                                                                            {ld.media && (
                                                                                <span className="px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-500/10 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-500/20 text-[10px]">
                                                                                Media: {ld.media}
                                                                            </span>
                                                                            )}
                                                                        </div>

                                                                        {/* Properties Grid */}
                                                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 text-[10px] bg-white dark:bg-gray-900/60 p-2.5 rounded border border-slate-200 dark:border-gray-800 shadow-sm dark:shadow-none">
                                                                            <div><span className="text-slate-500 dark:text-gray-400">Drive Type:</span> <span className="text-slate-800 dark:text-white font-medium">{ld.drive_type || "-"}</span></div>
                                                                            <div><span className="text-slate-500 dark:text-gray-400">Caching:</span> <span className="text-slate-800 dark:text-white font-medium">{ld.caching || "-"}</span></div>
                                                                            <div><span className="text-slate-500 dark:text-gray-400">Strip Size:</span> <span className="text-slate-800 dark:text-white font-medium">{ld.strip_size || "-"}</span></div>
                                                                            <div><span className="text-slate-500 dark:text-gray-400">Media Errors:</span> <span className={ld.media_errors > 0 ? "text-rose-600 dark:text-rose-400 font-bold" : "text-emerald-600 dark:text-emerald-400 font-semibold"}>{ld.media_errors ?? 0}</span></div>
                                                                            <div><span className="text-slate-500 dark:text-gray-400">Read Policy:</span> <span className="text-slate-800 dark:text-white font-medium">{ld.read_policy || "-"}</span></div>
                                                                            <div><span className="text-slate-500 dark:text-gray-400">Write Policy:</span> <span className="text-slate-800 dark:text-white font-medium">{ld.write_policy || "-"}</span></div>
                                                                        </div>

                                                                        {/* Collapsible Partitions & Member Physical Drives */}
                                                                        <LogicalDriveDetailsSection mountPoints={mountPoints} nestedPhysical={nestedPhysical} />
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Right Column: Physical Drives */}
                                            {physicalDrives.length > 0 && (
                                                <div className="space-y-3">
                                                    <h4 className="text-xs font-bold text-slate-900 dark:text-white flex items-center uppercase tracking-wide gap-1.5 border-b border-cyan-200 dark:border-cyan-500/20 pb-1.5">
                                                        <HardDrive className="w-3.5 h-3.5 text-orange-500" /> Physical Drives ({physicalDrives.length})
                                                    </h4>
                                                    <div className="space-y-3">
                                                        {physicalDrives.map((drive: any, driveidx: number) => {
                                                            const statusText = drive.status || drive.state || "OK";

                                                            return (
                                                                <div key={driveidx} className="bg-white dark:bg-gray-900/50 border border-slate-200 dark:border-gray-700/80 p-3 rounded-md flex flex-col justify-between space-y-2 text-xs shadow-sm dark:shadow-none">
                                                                    <div className="flex items-start justify-between gap-2">
                                                                        <div className="flex items-center gap-2 min-w-0">
                                                                            <div className={`w-2 h-2 rounded-full shrink-0 ${statusText === "OK" || statusText === "Healthy" ? "bg-emerald-500 dark:bg-emerald-400" : "bg-rose-500 dark:bg-rose-400"}`}></div>
                                                                            <div className="truncate">
                                                                                <p className="text-slate-900 dark:text-white font-bold text-[11px] leading-tight truncate">
                                                                                    {drive.model || "Physical Drive"}
                                                                                </p>
                                                                                <p className="text-[10px] text-slate-500 dark:text-gray-400 font-mono">
                                                                                    Location: {drive.location} · Size: {drive.size}
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                        <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase border shrink-0 ${healthBgBorder(statusText)}`}>
                                                                            {statusText}
                                                                        </span>
                                                                    </div>

                                                                    <div className="grid grid-cols-2 gap-1.5 text-[10px] font-mono text-slate-500 dark:text-gray-400 pt-1.5 border-t border-slate-100 dark:border-gray-800">
                                                                        <div>Interface: <span className="text-slate-700 dark:text-gray-200">{drive.interface_type || drive.media_type || "NVMe"}</span></div>
                                                                        <div>Firmware: <span className="text-slate-700 dark:text-gray-200">{drive.firmware_revision || "-"}</span></div>
                                                                        <div>Hot Spare: <span className="text-slate-700 dark:text-gray-200">{drive.hot_spare || "No"}</span></div>
                                                                        <div>Predicted Fail: <span className={drive.failure_predicted === "Yes" ? "text-rose-600 dark:text-rose-400 font-bold" : "text-emerald-600 dark:text-emerald-400 font-semibold"}>{drive.failure_predicted || "No"}</span></div>
                                                                        {drive.serial_number && drive.serial_number !== "Unknown" && (
                                                                            <div className="col-span-2 truncate" title={drive.serial_number}>
                                                                                S/N: <span className="text-slate-700 dark:text-gray-300">{drive.serial_number}</span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Controller Issues */}
                                    {controller.issues?.length > 0 && (
                                        <div className="border border-rose-200 dark:border-rose-500/20 rounded p-2.5 bg-rose-50/60 dark:bg-rose-500/5 shadow-sm dark:shadow-none">
                                            <h4 className="text-rose-700 dark:text-rose-300 text-[10px] font-bold mb-1.5 uppercase tracking-wider flex items-center">
                                                <AlertTriangle className="w-3 h-3 mr-1.5 text-rose-600 dark:text-rose-400" /> Controller Issues ({controller.issues.length})
                                            </h4>
                                            <div className="space-y-1">
                                                {controller.issues.map((issue: string, issueIdx: number) => (
                                                    <div key={issueIdx} className="flex items-start space-x-1.5 text-[11px] leading-tight text-rose-800 dark:text-rose-200">
                                                        <XCircle className="w-3 h-3 text-rose-600 dark:text-rose-400 shrink-0 mt-0.5" />
                                                        <span>{issue}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default Raid;
